import CloseIcon from "@mui/icons-material/Close";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  CircularProgress,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
} from "@mui/material";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { useSnackbar } from "notistack";
import React, { useCallback, useEffect, useRef, useState } from "react";

import { ApiIdentityResponse, useApiIdentities } from "api/services/useApiIdentities";
import { AddIcon } from "assets/icons";
import { SkeletonIndicator } from "components/common/AnimatedWebcam/SkeletonIndicator";
import { useAppDispatch, useAppSelector } from "hooks/store";
import { GroundlightButton } from "new-components/buttons/GroundlightButton";
import { ConfirmDeleteModal } from "new-components/modals/ConfirmDeleteModal";
import { Typography } from "new-components/typography/Typography";
import { getApiIdentities } from "store/services/ApiIdentities/apiIdentitiesThunks";
import {
  setIncludeRevoked,
  setOrdering,
  setPage,
  setRowsPerPage,
  setSearch,
} from "store/slices/ApiIdentities/apiIdentitiesSlice";

interface ApiTokenTableListProps {
  onOpenCreateDialog: () => void;
}

dayjs.extend(relativeTime);

const ROWS = [10, 25, 50];
const LOCAL_DATETIME_FORMAT = "YYYY-MM-DD HH:mm:ss";

type SortableColumn = "name" | "created_at" | "last_used_at" | "expires_at";

/** Returns the local timezone abbreviation (e.g. PST) for a date. */
function getLocalTimezoneAbbreviation(date: Date): string {
  const parts = new Intl.DateTimeFormat("en-US", { timeZoneName: "short" }).formatToParts(date);
  return parts.find((part) => part.type === "timeZoneName")?.value ?? "";
}

/** Formats an API datetime in the user's local timezone. */
function formatLocalDateTime(isoString: string | null | undefined): string | null {
  if (!isoString) {
    return null;
  }
  const parsed = dayjs(isoString);
  if (!parsed.isValid()) {
    return null;
  }
  return parsed.format(LOCAL_DATETIME_FORMAT);
}

/** Same as formatLocalDateTime, with a local timezone abbreviation for tooltips. */
function formatLocalDateTimeWithTz(isoString: string | null | undefined): string | null {
  const formatted = formatLocalDateTime(isoString);
  if (!formatted || !isoString) {
    return null;
  }
  const parsed = dayjs(isoString);
  if (!parsed.isValid()) {
    return null;
  }
  const tzAbbr = getLocalTimezoneAbbreviation(parsed.toDate());
  return tzAbbr ? `${formatted} ${tzAbbr}` : formatted;
}

function formatLastUsed(lastUsedAt: string | null): string {
  return formatLocalDateTime(lastUsedAt) ?? "Never";
}

/** Formats token_ttl seconds for the "Rotation" column. */
function formatTokenTtl(tokenTtlSeconds: number | null): string {
  if (tokenTtlSeconds === null) {
    return "Never";
  }
  const days = tokenTtlSeconds / (24 * 60 * 60);
  if (Number.isInteger(days) && days >= 1) {
    return `${days} day${days === 1 ? "" : "s"}`;
  }
  const hours = tokenTtlSeconds / (60 * 60);
  if (Number.isInteger(hours) && hours >= 1) {
    return `${hours} hour${hours === 1 ? "" : "s"}`;
  }
  const minutes = tokenTtlSeconds / 60;
  if (Number.isInteger(minutes) && minutes >= 1) {
    return `${minutes} minute${minutes === 1 ? "" : "s"}`;
  }
  return `${tokenTtlSeconds} second${tokenTtlSeconds === 1 ? "" : "s"}`;
}

function escapeHtml(str: string): string {
  return str.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}

function isIdentityAccessExpired(identity: ApiIdentityResponse): boolean {
  return !!identity.expires_at && dayjs(identity.expires_at).isBefore(dayjs());
}

function buildRevokeModalContent(identity: ApiIdentityResponse): string {
  const escapedName = escapeHtml(identity.name);
  const usage = identity.last_used_at
    ? `Last used ${dayjs(identity.last_used_at).fromNow()}.`
    : `This access has never been used.`;
  return `<strong>"${escapedName}"</strong> will be permanently revoked.<br/><br/>Any systems using it will lose access.<br/><br/>${usage}`;
}

interface SortState {
  orderBy: SortableColumn;
  direction: "asc" | "desc";
}

function orderingToSortState(ordering: string): SortState {
  if (ordering.startsWith("-")) {
    return { orderBy: ordering.slice(1) as SortableColumn, direction: "desc" };
  }
  return { orderBy: ordering as SortableColumn, direction: "asc" };
}

function sortStateToOrdering(orderBy: SortableColumn, direction: "asc" | "desc"): string {
  return direction === "desc" ? `-${orderBy}` : orderBy;
}

export function ApiTokenTableList({ onOpenCreateDialog }: ApiTokenTableListProps) {
  const [revokeModalOpen, setRevokeModalOpen] = useState<boolean>(false);
  const [selectedIdentity, setSelectedIdentity] = useState<ApiIdentityResponse | null>(null);
  const [searchInput, setSearchInput] = useState<string>("");

  const {
    data: { results: apiIdentities, count },
    loading: isFetching,
    page,
    rowsPerPage,
    ordering,
    search,
    includeRevoked,
    error: listError,
  } = useAppSelector((state) => state.apiIdentities);

  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useAppDispatch();
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const listQueryRef = useRef({ rowsPerPage, ordering, includeRevoked, search });
  listQueryRef.current = { rowsPerPage, ordering, includeRevoked, search };

  const { revokeApiIdentityAsync } = useApiIdentities();
  const [revokingIdentityId, setRevokingIdentityId] = useState<string | null>(null);

  const sortState = orderingToSortState(ordering);

  useEffect(() => {
    if (tableContainerRef.current) {
      tableContainerRef.current.scrollTop = 0;
    }
  }, [apiIdentities]);

  useEffect(() => {
    const timer = setTimeout(() => {
      const { rowsPerPage: pageSize, ordering: currentOrdering, includeRevoked: revoked } = listQueryRef.current;
      if (searchInput !== listQueryRef.current.search) {
        dispatch(setSearch(searchInput));
        dispatch(
          getApiIdentities({
            pageSize,
            ordering: currentOrdering,
            search: searchInput,
            includeRevoked: revoked,
          })
        );
        dispatch(setPage(0));
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput, dispatch]);

  const handleRevokeModalClose = () => {
    setSelectedIdentity(null);
    setRevokeModalOpen(false);
  };

  /** Revoke an identity and refresh the list. Used by the confirm modal and immediate expired revoke. */
  const revokeIdentity = async (identity: ApiIdentityResponse) => {
    setRevokingIdentityId(identity.id);
    try {
      await revokeApiIdentityAsync(identity.id);
      enqueueSnackbar(`Access revoked for "${identity.name}"`, { variant: "success" });
      handleRevokeModalClose();
      dispatch(getApiIdentities({ pageSize: rowsPerPage, ordering, search, includeRevoked }));
      dispatch(setPage(0));
    } catch {
      enqueueSnackbar("An error has occurred while trying to revoke", { variant: "error" });
    } finally {
      setRevokingIdentityId(null);
    }
  };

  const handleRevokeClick = async (identity: ApiIdentityResponse) => {
    // Already past Access expires: no confirmation — revoke immediately.
    if (isIdentityAccessExpired(identity)) {
      await revokeIdentity(identity);
      return;
    }
    setSelectedIdentity(identity);
    setRevokeModalOpen(true);
  };

  const handleClickRevoke = async () => {
    if (!selectedIdentity) {
      return;
    }
    await revokeIdentity(selectedIdentity);
  };

  /** Re-fetch the currently selected page after a list error. */
  const handleRetryFetch = () => {
    dispatch(
      getApiIdentities({
        pageSize: rowsPerPage,
        page: page + 1,
        ordering,
        search,
        includeRevoked,
      })
    );
  };

  const maxPage = Math.max(0, Math.ceil(count / rowsPerPage) - 1);

  const handleChangePage = (_event: unknown, newPage: number) => {
    const clampedPage = Math.max(0, Math.min(newPage, maxPage));
    dispatch(
      getApiIdentities({
        pageSize: rowsPerPage,
        page: clampedPage + 1,
        ordering,
        search,
        includeRevoked,
      })
    );
    dispatch(setPage(clampedPage));
  };

  const handleToggleRevoked = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newIncludeRevoked = event.target.checked;
    dispatch(setIncludeRevoked(newIncludeRevoked));
    dispatch(
      getApiIdentities({ pageSize: rowsPerPage, ordering, search, includeRevoked: newIncludeRevoked })
    );
    dispatch(setPage(0));
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newPageSize = parseInt(event.target.value, 10);
    dispatch(setRowsPerPage(newPageSize));
    dispatch(getApiIdentities({ pageSize: newPageSize, ordering, search, includeRevoked }));
    dispatch(setPage(0));
  };

  const handleSort = useCallback(
    (column: SortableColumn) => {
      let newDirection: "asc" | "desc" = "desc";
      if (sortState.orderBy === column && sortState.direction === "desc") {
        newDirection = "asc";
      }
      const newOrdering = sortStateToOrdering(column, newDirection);
      dispatch(setOrdering(newOrdering));
      dispatch(getApiIdentities({ pageSize: rowsPerPage, ordering: newOrdering, search, includeRevoked }));
      dispatch(setPage(0));
    },
    [sortState, rowsPerPage, search, includeRevoked, dispatch]
  );

  const sortLabel = (column: SortableColumn, label: string) => (
    <TableSortLabel
      active={sortState.orderBy === column}
      direction={sortState.orderBy === column ? sortState.direction : "desc"}
      onClick={() => handleSort(column)}
    >
      {label}
    </TableSortLabel>
  );

  return (
    <Box id="api-token-table-list" className="w-full h-full min-h-0 flex flex-col gap-2">
      <Box className="flex shrink-0 flex-wrap items-center gap-2">
        <Box sx={{ flex: "1 1 220px", minWidth: 0, maxWidth: 560 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search by name..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            InputProps={{
              inputProps: { "aria-label": "Search API tokens by name" },
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: searchInput.length > 0 && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchInput("")} aria-label="Clear search">
                    <CloseIcon fontSize="small" sx={{ opacity: "50%" }} />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>
        <Box className="flex flex-wrap items-center gap-2 ml-auto">
          <FormControlLabel
            control={<Switch checked={includeRevoked} onChange={handleToggleRevoked} size="small" />}
            label="Show revoked"
            sx={{ mr: 1 }}
          />
          <GroundlightButton onClick={onOpenCreateDialog} endIcon={AddIcon}>
            Create token
          </GroundlightButton>
        </Box>
      </Box>
      {listError && (
        <Box className="flex flex-col gap-2 items-start">
          <Typography variant="body" className="text-primary-darker">
            Failed to load API tokens.
          </Typography>
          <GroundlightButton onClick={handleRetryFetch} variant="plain">
            Retry
          </GroundlightButton>
        </Box>
      )}
      {!listError && isFetching && apiIdentities.length === 0 && (
        <Box className="flex justify-center items-center flex-1">
          <CircularProgress size={40} />
        </Box>
      )}
      {!listError && !isFetching && apiIdentities.length === 0 && (
        <Typography variant="body" className="text-primary-darker">
          {search ? "No tokens match your search." : "You don\u2019t have any tokens yet."}
        </Typography>
      )}
      <TableContainer
        className="min-h-0 flex-1 overflow-auto"
        ref={tableContainerRef}
        sx={{ display: listError || apiIdentities.length === 0 ? "none" : undefined }}
      >
        <Table stickyHeader aria-label="API identities table">
          <TableHead>
            <TableRow className="[&>th]:text-[14px] sm:[&>th]:text-[16px] [&>th]:font-bold [&>th]:max-w-[96px] sm:[&>th]:max-w-none [&>th]:px-1.5 sm:[&>th]:px-2.5 [&>th]:py-3">
              <TableCell align="center">{sortLabel("name", "Name")}</TableCell>
              <TableCell align="center">{sortLabel("created_at", "Created on")}</TableCell>
              <TableCell align="center">{sortLabel("last_used_at", "Last used")}</TableCell>
              <TableCell align="center">{sortLabel("expires_at", "Expiration")}</TableCell>
              <TableCell align="center">Rotation</TableCell>
              <TableCell align="center">Revoke</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {apiIdentities.map((identity, index) => (
              <TableRow
                key={identity.id}
                className="[&>td]:border-0 [&>td]:truncate [&>td]:max-w-[96px] sm:[&>td]:max-w-none [&>td]:px-1.5 sm:[&>td]:px-2.5 [&>td]:py-3"
                sx={[
                  index % 2 === 0 && { backgroundColor: "white" },
                  !!identity.revoked_at && { opacity: 0.65 },
                ]}
              >
                {revokingIdentityId === identity.id ? (
                  <TableCell colSpan={6} className="p-0">
                    {SkeletonIndicator({ height: 48.5, loading: true })}
                  </TableCell>
                ) : (
                  <>
                    <TableCell align="center">
                      <Tooltip title={identity.name}>
                        <span>{identity.name}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="center">
                      {formatLocalDateTime(identity.created_at) ? (
                        <Tooltip title={formatLocalDateTimeWithTz(identity.created_at) ?? ""}>
                          <span>{formatLocalDateTime(identity.created_at)}</span>
                        </Tooltip>
                      ) : (
                        ""
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {identity.last_used_at ? (
                        <Tooltip title={formatLocalDateTimeWithTz(identity.last_used_at) ?? ""}>
                          <span>{formatLastUsed(identity.last_used_at)}</span>
                        </Tooltip>
                      ) : (
                        "Never"
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {identity.expires_at ? (
                        <Tooltip title={formatLocalDateTimeWithTz(identity.expires_at) ?? ""}>
                          <span className={dayjs(identity.expires_at).isBefore(dayjs()) ? "text-red font-bold" : ""}>
                            {dayjs(identity.expires_at).isBefore(dayjs())
                              ? "Expired"
                              : formatLocalDateTime(identity.expires_at)}
                          </span>
                        </Tooltip>
                      ) : (
                        "Never"
                      )}
                    </TableCell>
                    <TableCell align="center">{formatTokenTtl(identity.token_ttl)}</TableCell>
                    <TableCell align="center">
                      {!identity.revoked_at && (
                        <Tooltip title="Revokes this access forever">
                          <IconButton
                            onClick={() => handleRevokeClick(identity)}
                            className="p-0"
                            data-testid="RevokeIcon"
                          >
                            <DeleteForeverIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {!listError && apiIdentities.length > 0 && (
        <TablePagination
          className="shrink-0"
          rowsPerPageOptions={ROWS}
          component="div"
          count={count}
          rowsPerPage={rowsPerPage}
          page={Math.min(page, maxPage)}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          slotProps={{
            actions: {
              previousButton: { disabled: isFetching || page === 0 },
              nextButton: { disabled: isFetching || page >= maxPage },
            },
          }}
          sx={{
            ".MuiToolbar-root": {
              padding: "0px",
            },
            ".MuiTablePagination-actions": {
              marginLeft: "16px",
            },
          }}
        />
      )}
      {revokeModalOpen && selectedIdentity && (
        <ConfirmDeleteModal
          isDeleteModalOpen={revokeModalOpen}
          isLoadingIQDelete={!!revokingIdentityId}
          modalContent={buildRevokeModalContent(selectedIdentity)}
          modalTitle="Revoke API access"
          setIsDeleteModalOpen={setRevokeModalOpen}
          onRemoveHandler={handleClickRevoke}
          buttonTexts={{ cancelText: "Cancel", successText: "Revoke" }}
        />
      )}
    </Box>
  );
}
